/** Avis des invités — formes renvoyées par l'API (`/public/feedback`, `/admin/.../feedback`). */

export type RatingKey = 'music' | 'decor' | 'organization' | 'activities' | 'overall' | 'service'
export type RecommendationKey = 'yes' | 'probably' | 'unsure' | 'no'
export type TextField = 'liked' | 'disliked' | 'improve' | 'suggestions'

export const RATING_KEYS: RatingKey[] = [
  'music',
  'decor',
  'organization',
  'activities',
  'overall',
  'service',
]
export const RECOMMENDATION_KEYS: RecommendationKey[] = ['yes', 'probably', 'unsure', 'no']
export const TEXT_FIELDS: TextField[] = ['liked', 'disliked', 'improve', 'suggestions']

export interface FeedbackChoice<K extends string = string> {
  key: K
  label: string
}

export interface FeedbackForm {
  event: {
    slug: string
    name: string
    logo_url: string | null
    theme: Record<string, string>
    locales: string[]
    locale: string
  }
  is_open: boolean
  /** Tous les textes du formulaire, déjà dans la langue demandée. */
  texts: Record<string, string>
  categories: FeedbackChoice<RatingKey>[]
  recommendations: FeedbackChoice<RecommendationKey>[]
  form_token: string | null
  max_length: number
  min_fill_seconds: number
}

export interface FeedbackAnswer {
  form_token: string
  score: number
  ratings: Record<RatingKey, number>
  recommendation: RecommendationKey
  liked: string | null
  disliked: string | null
  improve: string | null
  suggestions: string | null
  locale: string
  website: string | null
}

// ── Administration ─────────────────────────────────────────────────

export interface FeedbackResponseRow {
  id: string
  created_at: string
  score: number
  ratings: Record<RatingKey, number>
  recommendation: RecommendationKey
  liked: string | null
  disliked: string | null
  improve: string | null
  suggestions: string | null
  locale: string
  is_read: boolean
}

export interface FeedbackResponsePage {
  items: FeedbackResponseRow[]
  total: number
  page: number
  page_size: number
  pages: number
  unread: number
}

export interface FeedbackTerm {
  term: string
  count: number
}

export interface FeedbackAnalysis {
  count: number
  score: {
    average: number | null
    median: number | null
    distribution: number[]
    enthusiasts: number
    satisfied: number
    disappointed: number
  }
  recommendation: {
    choices: { key: RecommendationKey; label: string; count: number; share: number }[]
    index: number | null
  }
  categories: {
    key: RatingKey
    label: string
    average: number | null
    rank: number
    verdict: 'strength' | 'weakness' | 'relative_weakness' | 'neutral'
  }[]
  days: { day: string; count: number; average: number }[]
  texts: {
    field: TextField
    answered: number
    words: FeedbackTerm[]
    expressions: FeedbackTerm[]
    quotes: string[]
  }[]
  summary: string
  strengths: string[]
  improvements: string[]
}

export interface FeedbackText {
  key: string
  group: 'page' | 'score' | 'categories' | 'texts' | 'recommend' | 'end'
  multiline: boolean
  default: Record<string, string>
  value: Record<string, string>
}

export interface FeedbackSettings {
  is_open: boolean
  public_url: string
  texts: FeedbackText[]
  max_length: number
  recipients: number
}

export interface InvitationPlan {
  eligible: number
  already_invited: number
  to_send: number
  consent_only: boolean
  public_url: string
}
